import { useState, type ReactNode } from 'react';
import type { ApiError } from '../api';
import {
  IDIOMAS,
  NOMBRE_DE_IDIOMA,
  idiomaActual,
  texto,
  useT,
  type Clave,
  type Idioma,
} from '../i18n';

/**
 * Componentes base del panel.
 *
 * Todo lo interactivo es un elemento nativo (`button`, `a`, `input`) con su rol y su
 * foco por defecto. Reconstruir controles con `div` obliga a reimplementar teclado y
 * accesibilidad, y casi siempre queda peor que lo que el navegador ya trae.
 */

// ── Iconografía ─────────────────────────────────────────────────────────────────

/**
 * Iconos dibujados a mano, sin dependencia externa.
 *
 * Son ocho trazos: una librería de iconos pesaría más que toda la hoja de estilos y
 * traería tres mil glifos que nadie va a usar. Todos comparten grilla de 24, trazo de 1,75
 * y extremos redondeados, que es lo que hace que se lean como un juego y no como un
 * collage.
 */
const ICON_PATHS = {
  /** Guía de arranque: un libro abierto. */
  book: 'M12 6.5C10.5 5 8.5 4.5 4 4.5V18c4.5 0 6.5.5 8 2 1.5-1.5 3.5-2 8-2V4.5c-4.5 0-6.5.5-8 2Zm0 0V20',
  /** Cuentas: dos personas. No reusa la llave, que ya significa credenciales. */
  users:
    'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm0 0c-3 0-5.5 1.8-5.5 4.5V20h11v-4.5C14.5 12.8 12 11 9 11Zm7-6.7a3.5 3.5 0 0 1 0 6.4M17 11.3c2.2.5 3.5 2.1 3.5 4.2V20',
  home: 'M3 10.5 12 3l9 7.5M5.5 9.5V20a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9.5',
  plus: 'M12 5v14M5 12h14',
  check: 'M4 12.5 9 17.5 20 6.5',
  gauge: 'M12 14.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm1.4-3.4L17 7.5M4 19a9 9 0 1 1 16 0',
  plug: 'M9 3v5M15 3v5M6 8h12v3a6 6 0 0 1-6 6 6 6 0 0 1-6-6V8ZM12 17v4',
  exit: 'M15 4h3a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-3M10 16l-4-4 4-4M6 12h11',
  sun: 'M12 4V2m0 20v-2m8-8h2M2 12h2m13.7-5.7 1.4-1.4M4.9 19.1l1.4-1.4m11.4 0 1.4 1.4M4.9 4.9l1.4 1.4M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z',
  moon: 'M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z',
  spark: 'M12 3v6m0 6v6m-9-9h6m6 0h6M6.3 6.3l3.2 3.2m5 5 3.2 3.2m0-11.4-3.2 3.2m-5 5-3.2 3.2',
  key: 'M15.5 8.5a3.5 3.5 0 1 1-3.4 4.4L11 14H9v2H7v2H4v-3l5.6-5.6a3.5 3.5 0 0 1 5.9-.9Z',
  flask: 'M9.5 3v5.2L4.8 17a2 2 0 0 0 1.8 3h10.8a2 2 0 0 0 1.8-3l-4.7-8.8V3M8 3h8M8 12h8',
  star: 'm12 3.5 2.6 5.6 6.1.8-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.9l6.1-.8L12 3.5Z',
  eye: 'M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Zm10 2.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
  'eye-off':
    'M3 3l18 18M10.6 10.7a2.5 2.5 0 0 0 3.4 3.4M6.7 6.9C3.9 8.6 2 12 2 12s3.6 6.5 10 6.5c1.7 0 3.2-.4 4.5-1M9.9 5.6A11 11 0 0 1 12 5.5c6.4 0 10 6.5 10 6.5a18 18 0 0 1-2.9 3.6',
  /* Los tres del sistema de avisos. Círculo de radio 9, igual que el resto de la grilla. */
  alert: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm0 4.5v5.5m0 3h.01',
  info: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm0 5h.01M12 11v5.5',
  close: 'M6 6l12 12M18 6 6 18',
  globe:
    'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm0 0c-2.5 2.4-3.8 5.4-3.8 9s1.3 6.6 3.8 9m0-18c2.5 2.4 3.8 5.4 3.8 9s-1.3 6.6-3.8 9M3.3 9h17.4M3.3 15h17.4',
  chevron: 'm6 9 6 6 6-6',
  /** «Más»: tres líneas. El icono que todo el mundo ya sabe leer. */
  menu: 'M4 7h16M4 12h16M4 17h16',
  dice: 'M5 4h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm3.5 4.5h.01m6.99 0h.01M11.99 12h.01M8.5 15.5h.01m6.99 0h.01',
} as const;

export type IconName = keyof typeof ICON_PATHS;

export function Icon({ name, size = 18 }: { name: IconName; size?: number }) {
  return (
    <svg
      // Decorativo: en cada uso hay una etiqueta de texto o un `aria-label` al lado.
      aria-hidden="true"
      focusable="false"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      <path d={ICON_PATHS[name]} />
    </svg>
  );
}

// ── Tema ────────────────────────────────────────────────────────────────────────

/**
 * Interruptor de tema claro/oscuro.
 *
 * Lee del DOM en vez de mantener su propio estado inicial: el tema ya lo fijó el script
 * de `index.html` antes del primer pintado, y duplicar esa decisión en React sería tener
 * dos fuentes de verdad que se contradicen durante un cuadro.
 */
export function ThemeToggle() {
  const { t } = useT();
  const [dark, setDark] = useState(() => document.documentElement.dataset.theme !== 'light');

  const toggle = (): void => {
    const next = dark ? 'light' : 'dark';
    document.documentElement.dataset.theme = next;
    try {
      localStorage.setItem('vcos-theme', next);
    } catch {
      // Modo privado: el tema vale para esta sesión y no se recuerda. No es un error
      // que valga la pena mostrarle a nadie.
    }
    setDark(!dark);
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={t(dark ? 'comun.temaClaro' : 'comun.temaOscuro')}
      className="rounded-lg p-2 text-[var(--color-ink-muted)] transition hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink)]"
    >
      <Icon name={dark ? 'sun' : 'moon'} />
    </button>
  );
}

// ── Idioma ──────────────────────────────────────────────────────────────────────

/**
 * Selector de idioma.
 *
 * Cada idioma se ofrece escrito en sí mismo —«Español», «English»— y no traducido al idioma
 * activo. Quien no entiende el idioma en el que quedó la pantalla necesita reconocer el suyo
 * para salir, y «Spanish» no le sirve a quien busca «Español».
 */
export function LanguageToggle() {
  const { idioma, setIdioma } = useT();

  return (
    <label className="flex items-center gap-1.5 text-[var(--color-ink-muted)]">
      <span className="sr-only">{NOMBRE_DE_IDIOMA[idioma]}</span>
      <Icon name="globe" size={16} />
      <select
        value={idioma}
        onChange={(event) => setIdioma(event.target.value as Idioma)}
        className="cursor-pointer rounded-lg bg-transparent py-1 pr-1 text-sm transition hover:text-[var(--color-ink)]"
      >
        {IDIOMAS.map((clave) => (
          <option key={clave} value={clave}>
            {NOMBRE_DE_IDIOMA[clave]}
          </option>
        ))}
      </select>
    </label>
  );
}

// ── Superficies ─────────────────────────────────────────────────────────────────

/**
 * Una sección plegada.
 *
 * ── Por qué existe ──────────────────────────────────────────────────────────────
 * El formulario de nueva influencer llegó a mostrar cuarenta y ocho nichos, cincuenta rasgos y
 * cinco desplegables de estilo, todos abiertos, todos opcionales. Cada uno se había agregado por
 * un motivo bueno y el conjunto era imposible: para llegar al botón había que pasar de largo
 * ciento y pico de decisiones que nadie pidió tomar.
 *
 * Lo que tiene un valor por defecto que funciona no necesita estar a la vista. Necesita estar
 * **disponible**, que no es lo mismo.
 *
 * Es `<details>` nativo: el teclado, el foco y la búsqueda del navegador ya funcionan, y plegar
 * no saca el contenido del documento.
 */
export function Desplegable({
  titulo,
  detalle,
  children,
}: {
  titulo: string;
  detalle?: string;
  children: ReactNode;
}) {
  return (
    <details className="group rounded-2xl border border-[var(--color-border-subtle)]">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4 text-sm font-medium">
        <span>
          {titulo}
          {/*
            El detalle es una pastilla, no texto suelto al lado del título.

            Con `ml-2` a secas se leía pegado —«un modelo por etapa3 de 3 con modelo propio»— y
            había que separar dos frases distintas a ojo. Un fondo hundido y un poco de aire lo
            vuelven una etiqueta: se ve que es otra cosa antes de leerla.
          */}
          {detalle && (
            <span className="ml-2.5 rounded-md bg-[var(--color-surface-sunken)] px-2 py-0.5 align-middle font-normal text-xs text-[var(--color-ink-muted)]">
              {detalle}
            </span>
          )}
        </span>
        <span
          aria-hidden="true"
          className="text-[var(--color-ink-muted)] transition-transform group-open:rotate-180"
        >
          <Icon name="chevron" size={16} />
        </span>
      </summary>
      <div className="border-t border-[var(--color-border-subtle)] px-5 py-5">{children}</div>
    </details>
  );
}

export function Card({
  title,
  actions,
  children,
  className = '',
  accent = false,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Línea de acento arriba. Reservada para la tarjeta que exige una decisión. */
  accent?: boolean;
}) {
  return (
    /*
      `min-w-0`: una tarjeta puede encoger por debajo de su contenido.
      
      Sin esto, un hijo de rejilla o de flex trae `min-width: auto`, que significa «nunca más
      angosto que mi contenido». La tarjeta de influencers medía **484 px dentro de una pista de
      361**, y como el cuerpo recorta el desborde horizontal, lo que sobraba no se podía
      arrastrar ni ver: desaparecía. Rodrigo lo vio en su teléfono antes que ninguna medición
      mía, porque yo estaba comprobando si la página se deslizaba —y justamente no se deslizaba,
      se cortaba.
      
      Los `truncate` y `min-w-0` de adentro ya estaban puestos y no servían de nada: el que se
      negaba a encoger era el contenedor de todos ellos.
    */
    <section className={`panel min-w-0 p-5 ${accent ? 'accent-rule' : ''} ${className}`}>
      {/*
        Envuelve, y las acciones no se encogen.

        Con un título largo y dos botones al lado, el segundo se comprimía hasta mostrar una sola
        letra: se leía «E» donde decía «Escribir la ficha». Un botón recortado a un carácter no
        parece un botón apretado, parece un error de la pantalla.
      */}
      {(title || actions) && (
        <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
          {title && (
            <h2 className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-[var(--color-ink-faint)]">
              {title}
            </h2>
          )}
          {/*
            `shrink-0` acá desbordaba todas las tarjetas del panel.

            Le decía a las acciones «no encojas nunca», y con dos o tres botones eso estira la
            tarjeta más allá de la pantalla: medido, 500 px de contenido en 393 disponibles, y
            el desborde se propaga a la página entera. `flex-wrap` estaba puesto justamente
            para que bajaran de línea, y `shrink-0` se lo impedía — dos instrucciones opuestas
            sobre lo mismo, y ganaba la equivocada.

            Sin `shrink-0` bajan de línea cuando no entran, que es lo que `flex-wrap` prometía.
          */}
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: 'neutral' | 'ok' | 'warn' | 'danger';
}) {
  const toneColor = {
    neutral: 'var(--color-ink)',
    ok: 'var(--color-ok)',
    warn: 'var(--color-warn)',
    danger: 'var(--color-danger)',
  }[tone];

  // La franja del tono neutro no usa el color del texto: a ese contraste compite con el
  // número en vez de acompañarlo, y un dato sin estado no debería llamar la atención.
  const railColor = tone === 'neutral' ? 'var(--color-ink-faint)' : toneColor;

  return (
    /*
      Más compacta en el teléfono: el número baja de 32 a 26 px y la etiqueta deja de ir en
      versalitas espaciadas, que en 190 px de ancho parten «PUBLICACIONES PROGRAMADAS» en tres
      líneas y hacen que una cifra de dos dígitos ocupe media pantalla de alto.
    */
    <div className="panel panel-hover relative overflow-hidden p-3.5 sm:p-4">
      {/* Franja lateral del tono. Da el estado de un vistazo sin depender de leer el número. */}
      <span
        aria-hidden="true"
        className="absolute inset-y-3 left-0 w-0.5 rounded-full opacity-70"
        style={{ background: railColor }}
      />
      <div className="text-[0.65rem] font-semibold text-[var(--color-ink-faint)] sm:text-[0.7rem] sm:uppercase sm:tracking-[0.12em]">
        {label}
      </div>
      <div
        className="mt-1.5 text-[1.625rem] font-semibold leading-none tracking-tight tabular-nums sm:text-[2rem]"
        style={{ color: toneColor }}
      >
        {value}
      </div>
      {hint && <div className="mt-1.5 text-xs text-[var(--color-ink-muted)]">{hint}</div>}
    </div>
  );
}

export function Button({
  children,
  onClick,
  variant = 'primary',
  disabled,
  type = 'button',
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
  type?: 'button' | 'submit';
  title?: string;
}) {
  const styles = {
    primary:
      'accent-gradient text-[var(--color-accent-ink)] shadow-[0_4px_16px_-4px_var(--color-accent)] hover:brightness-110 hover:shadow-[0_6px_22px_-4px_var(--color-accent)]',
    secondary:
      'border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] text-[var(--color-ink)] hover:border-[var(--color-border-strong)] hover:bg-[var(--color-accent-soft)]',
    danger:
      'bg-[var(--color-danger)] text-white hover:brightness-110 shadow-[0_4px_16px_-4px_var(--color-danger)]',
  }[variant];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      /*
        `min-h-11` son 44 px: el mínimo que publican Apple y Google, y sale de medir dedos, no
        de estética. `py-2` daba 36 y se ve bien — se falla al pulsarlo, sobre todo en
        movimiento, que es donde se usa un teléfono. En escritorio no cambia nada visible: el
        botón ya medía casi eso con su texto adentro.

        `justify-center` importa por lo mismo: al crecer el alto, un botón ancho con el texto
        pegado arriba se ve desalineado.
      */
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-all duration-150 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none ${styles}`}
    >
      {children}
    </button>
  );
}

/** Estados del ciclo de vida, con color según lo que el administrador debe hacer. */
const STATUS_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'neutral'> = {
  ACTIVE: 'ok',
  READY: 'ok',
  DRAFT: 'neutral',
  GENERATING_IDENTITY: 'neutral',
  GENERATING_MEDIA: 'neutral',
  AWAITING_REVIEW: 'warn',
  WAITING_FOR_ACCOUNT_CREATION: 'warn',
  WAITING_FOR_EMAIL_VERIFICATION: 'warn',
  WAITING_FOR_KYC: 'warn',
  WAITING_FOR_OAUTH: 'warn',
  QUOTA_EXHAUSTED: 'warn',
  BLOCKED: 'neutral',
  PAUSED: 'neutral',
  ERROR: 'danger',
};

/**
 * Los estados se traducen por clave, no por tabla propia.
 *
 * La tabla estaba acá con los catorce rótulos en español. Una segunda tabla con los mismos
 * catorce en inglés sería exactamente lo que el diccionario existe para evitar.
 */
function estadoLegible(status: string, t: (clave: Clave) => string): string {
  const clave = `estado.${status}` as Clave;
  // Un estado que el servidor agregue y el panel todavía no conozca sale en crudo. Es feo y es
  // correcto: un hueco haría pensar que la influencer no tiene estado.
  const texto = t(clave);
  return texto === clave ? status : texto;
}

/** Estados en curso. Su punto late para distinguir «trabajando» de «esperándote». */
const RUNNING_STATUSES = new Set(['GENERATING_IDENTITY', 'GENERATING_MEDIA']);

/**
 * Nombre legible de cada acción humana.
 *
 * El panel imprimía la constante en crudo —`CREATE_DEVELOPER_APP`— y una tarea que se le
 * muestra a una persona para que la haga no debería estar escrita en el idioma de la base
 * de datos. Sin entrada, se cae al valor original: mejor una constante fea que un hueco.
 */
export function useTaskTypeLabel(): (type: string, platform?: string) => string {
  const { t } = useT();
  return (type, platform) => {
    /*
      Una plataforma puede llamar distinto al mismo paso.

      `CREATE_ACCOUNT` en Facebook NO es crear una cuenta: es crear una Página desde un perfil
      que ya existe. El título decía «Crear la cuenta» encima de un texto que empieza con «NO
      crees una cuenta» — y entre el título y el cuerpo, el título gana, porque es lo que se lee
      primero y lo que queda en la lista.

      Se busca primero la clave con plataforma y se cae a la general, así que agregar una
      excepción es agregar una línea al diccionario y nada más.
    */
    if (platform) {
      const propia = `tarea.${type}.${platform}` as Clave;
      const texto = t(propia);
      if (texto !== propia) return texto;
    }

    const clave = `tarea.${type}` as Clave;
    const texto = t(clave);
    return texto === clave ? type : texto;
  };
}

export function StatusBadge({ status }: { status: string }) {
  const { t } = useT();
  const tone = STATUS_TONE[status] ?? 'neutral';
  const color = {
    ok: 'var(--color-ok)',
    warn: 'var(--color-warn)',
    danger: 'var(--color-danger)',
    neutral: 'var(--color-ink-muted)',
  }[tone];

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium"
      style={{
        color,
        borderColor: `color-mix(in oklab, ${color} 35%, transparent)`,
        background: `color-mix(in oklab, ${color} 12%, transparent)`,
      }}
    >
      {/* El punto duplica la información del color, que por sí solo no alcanza. */}
      <span
        aria-hidden="true"
        className={
          'h-1.5 w-1.5 rounded-full ' +
          (RUNNING_STATUSES.has(status) ? 'motion-safe:animate-pulse' : '')
        }
        style={{ background: color }}
      />
      {estadoLegible(status, t)}
    </span>
  );
}

export function ErrorBanner({ error, onRetry }: { error: ApiError; onRetry?: () => void }) {
  const { t } = useT();

  return (
    <div
      role="alert"
      className="rounded-2xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/10 p-4 backdrop-blur"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-[var(--color-danger)]">{error.message}</p>
          <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
            {t('comun.codigo', { code: error.code })}
          </p>
          {Array.isArray(error.details?.violations) && (
            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-[var(--color-ink-muted)]">
              {(error.details.violations as { rule: string; message: string }[]).map(
                (violation) => (
                  <li key={`${violation.rule}-${violation.message}`}>{violation.message}</li>
                ),
              )}
            </ul>
          )}
        </div>
        {onRetry && (
          <Button variant="secondary" onClick={onRetry}>
            {t('comun.reintentar')}
          </Button>
        )}
      </div>
    </div>
  );
}

export function Loading({ label }: { label?: string }) {
  const { t } = useT();
  const texto = label ?? t('comun.cargando');

  return (
    <div
      className="flex items-center gap-3 p-6 text-sm text-[var(--color-ink-muted)]"
      aria-live="polite"
    >
      <span
        className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--color-border-subtle)] border-t-[var(--color-accent)]"
        aria-hidden="true"
      />
      {texto}
    </div>
  );
}

export function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-[var(--color-border-subtle)] px-6 py-10 text-center">
      <span
        aria-hidden="true"
        className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--color-surface-sunken)] text-[var(--color-ink-faint)]"
      >
        <Icon name="spark" size={20} />
      </span>
      <p className="text-sm font-medium">{title}</p>
      <p className="mx-auto mt-1 max-w-sm text-sm text-[var(--color-ink-muted)]">{description}</p>
    </div>
  );
}

/** Barra de uso de cuota. Cambia de color al acercarse al límite. */
export function QuotaBar({ used, limit }: { used: number; limit: number }) {
  const percent = limit === 0 ? 100 : Math.min(100, (used / limit) * 100);
  const color =
    percent >= 90 ? 'var(--color-danger)' : percent >= 70 ? 'var(--color-warn)' : 'var(--color-ok)';

  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(percent)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${texto('comun.cuotaUsada', { n: Math.round(percent) })}`}
      className="h-2 w-full overflow-hidden rounded-full bg-[var(--color-surface-sunken)] ring-1 ring-inset ring-[var(--color-border-subtle)]"
    >
      <div
        className="h-full rounded-full transition-[width] duration-500 ease-out"
        style={{
          width: `${percent}%`,
          // Degradado del propio tono: el borde de avance queda más claro que el resto,
          // que es lo que hace legible dónde termina la barra sobre fondo oscuro.
          background: `linear-gradient(90deg, color-mix(in oklab, ${color} 65%, transparent), ${color})`,
          boxShadow: `0 0 12px -2px ${color}`,
        }}
      />
    </div>
  );
}

/** Botón de copiar al portapapeles, con confirmación visible. */
export function CopyButton({ text, label }: { text: string; label?: string }) {
  const { t } = useT();
  return (
    <Button
      variant="secondary"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          // Confirmación mínima y sin librerías: el propio botón lo dice.
        });
      }}
    >
      {label ?? t('comun.copiar')}
    </Button>
  );
}

/**
 * Cuánto hace de un instante pasado, en palabras.
 *
 * Una fecha absoluta obliga a hacer la resta mentalmente para responder la única pregunta que
 * importa donde se usa —«¿hace mucho de esto?»—, y esa resta se hace mal cuando la lista tiene
 * veinte filas. `null` es «nunca», que muchas veces no es un error sino el estado inicial.
 */
export function haceCuanto(iso: string | null): string {
  if (!iso) return texto('tiempo.nunca');

  const minutos = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutos < 1) return texto('tiempo.recien');
  if (minutos < 60) return texto('tiempo.haceMin', { n: minutos });

  const horas = Math.floor(minutos / 60);
  if (horas < 24) return texto('tiempo.haceH', { n: horas });

  const dias = Math.floor(horas / 24);
  if (dias === 1) return texto('tiempo.ayer');
  if (dias < 30) return texto('tiempo.haceDias', { n: dias });

  const meses = Math.floor(dias / 30);
  return meses === 1 ? texto('tiempo.haceUnMes') : texto('tiempo.haceMeses', { n: meses });
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(idiomaActual(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Tiempo relativo hasta un instante futuro. Se usa para el reinicio de cuota. */
export function timeUntil(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return texto('tiempo.yaDisponible');

  const hours = Math.floor(diff / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  if (hours > 0) return texto('tiempo.enHM', { h: hours, m: minutes });
  return texto('tiempo.enM', { m: minutes });
}

/**
 * Avatar circular de una influencer.
 *
 * Con imagen muestra la foto; sin ella, las iniciales sobre un color derivado del nombre.
 * El color es determinista —el mismo nombre da siempre el mismo tono— para que una
 * influencer sin foto siga siendo reconocible de un vistazo en el listado.
 */
export function Avatar({
  name,
  url,
  size = 40,
}: {
  name: string;
  url: string | null;
  size?: number;
}) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  if (url) {
    return (
      <img
        // Al tamaño en que se dibuja y no al original: veinte avatares de 40 px pedían
        // treinta megas para llenar veinte círculos.
        src={miniatura(url, size)}
        // Vacío a propósito: el nombre ya está en el texto contiguo, y repetirlo hace que
        // un lector de pantalla lo anuncie dos veces.
        alt=""
        width={size}
        height={size}
        loading="lazy"
        className="shrink-0 rounded-full object-cover ring-1 ring-[var(--color-border-subtle)]"
        style={{ width: size, height: size }}
      />
    );
  }

  let hue = 0;
  for (let i = 0; i < name.length; i++) hue = (hue * 31 + name.charCodeAt(i)) % 360;

  return (
    <span
      aria-hidden="true"
      className="flex shrink-0 items-center justify-center rounded-full font-semibold text-white ring-1 ring-white/10"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.36,
        background: `linear-gradient(140deg, oklch(66% 0.14 ${hue}), oklch(52% 0.15 ${(hue + 40) % 360}))`,
      }}
    >
      {initials || '?'}
    </span>
  );
}

/**
 * Estados que no representan una influencer utilizable.
 *
 * `ERROR` son corridas que fallaron; `DRAFT`, las que nunca llegaron a generar nada. Se
 * ocultan del listado por defecto porque son ruido de depuración, pero nunca se borran ni
 * se esconden sin decirlo: el filtro muestra cuántas hay.
 */
export const INCOMPLETE_STATUSES: readonly string[] = ['ERROR', 'DRAFT'];

export function isIncomplete(status: string): boolean {
  return INCOMPLETE_STATUSES.includes(status);
}

// ── Tablas que sobreviven a un teléfono ─────────────────────────────────────────

/** Una columna: cómo se titula, cómo se dibuja, y qué papel cumple en la tarjeta. */
export type Columna<T> = {
  clave: string;
  encabezado: ReactNode;
  celda: (fila: T) => ReactNode;
  /** Cifras: a la derecha en la tabla ancha. */
  numerica?: boolean;
  /**
   * Esta columna encabeza la tarjeta del teléfono, sin etiqueta delante.
   *
   * Una sola por tabla. Es lo que identifica la fila —el nombre de la influencer, el modelo—
   * y ponerle «Influencer:» adelante sería rotular lo único que no necesita rótulo.
   */
  titulo?: boolean;
  /** No aparece en la tarjeta. Para columnas que sólo tienen sentido comparando filas. */
  soloAncha?: boolean;
};

/**
 * Una tabla arriba de `md`; una lista de tarjetas debajo.
 *
 * ── Por qué no alcanza con deslizar ────────────────────────────────────────────
 * La tabla de plataformas tiene nueve columnas y declaraba `min-w-[52rem]`: 832 px dentro de
 * un `overflow-x-auto`. En una pantalla de 390 px eso significa que para leer una fila hay que
 * arrastrarla de a poco, y que **el encabezado se va de la pantalla antes que el dato**. Cuando
 * llegás a la séptima columna ya no sabés cuál es: quedan nueve números sin nombre.
 *
 * Deslizar sirve cuando lo que sobra es un poco. Con nueve columnas no sobra un poco: sobra la
 * mitad de la información, y la que sobra es la que dice qué significa la otra.
 *
 * ── Por qué tarjetas y no columnas escondidas ──────────────────────────────────
 * Esconder columnas en el teléfono es decidir por alguien que su dato no le importa. La tarjeta
 * los muestra todos, cada uno con su nombre al lado, apilados. Ocupa más alto —y el alto es lo
 * único que sobra en un teléfono— a cambio de que nada se pierda.
 */
export function TablaResponsiva<T>({
  columnas,
  filas,
  claveDe,
  anchoMinimo = '52rem',
}: {
  columnas: readonly Columna<T>[];
  filas: readonly T[];
  claveDe: (fila: T) => string;
  /** Ancho por debajo del cual la tabla ancha se desliza. Sólo aplica de `md` para arriba. */
  anchoMinimo?: string;
}) {
  const titulo = columnas.find((columna) => columna.titulo);
  const resto = columnas.filter((columna) => !columna.titulo && !columna.soloAncha);

  return (
    <>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm" style={{ minWidth: anchoMinimo }}>
          <thead>
            <tr className="border-b border-[var(--color-border-subtle)] text-left text-xs text-[var(--color-ink-muted)]">
              {columnas.map((columna) => (
                <th
                  key={columna.clave}
                  className={'pb-2 font-medium ' + (columna.numerica ? 'text-right' : '')}
                >
                  {columna.encabezado}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-border-subtle)]">
            {filas.map((fila) => (
              <tr key={claveDe(fila)} className="align-top">
                {columnas.map((columna) => (
                  <td
                    key={columna.clave}
                    className={'py-3 ' + (columna.numerica ? 'text-right tabular-nums' : '')}
                  >
                    {columna.celda(fila)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="space-y-3 md:hidden">
        {filas.map((fila) => (
          <li
            key={claveDe(fila)}
            className="rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)]/60 p-4"
          >
            {titulo && <div className="text-sm font-medium">{titulo.celda(fila)}</div>}
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5">
              {resto.map((columna) => (
                <div key={columna.clave} className="min-w-0">
                  <dt className="text-[0.6875rem] text-[var(--color-ink-faint)]">
                    {columna.encabezado}
                  </dt>
                  <dd className="mt-0.5 text-sm tabular-nums">{columna.celda(fila)}</dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * La URL de una imagen, pedida al ancho que de verdad se va a dibujar.
 *
 * ── Qué problema resuelve ──────────────────────────────────────────────────────
 * Las tomas se guardan como PNG de 1024×1280 —1,5 MB de promedio— y la galería las pedía
 * enteras para dibujarlas de 200 px. La ficha de una influencer con dieciocho tomas bajaba
 * 27 MB para llenar dieciocho cuadraditos, y tardaba lo que se tarda en bajar 27 MB.
 *
 * `?w=` hace que el Worker las transforme y las devuelva en WebP al ancho pedido.
 *
 * ── Por qué el doble ──────────────────────────────────────────────────────────
 * Porque una pantalla con densidad doble dibuja dos píxeles físicos por cada uno de CSS, y una
 * miniatura pedida al ancho exacto se ve borrosa justo en las pantallas donde la gente mira
 * fotos. Se pide el doble y se acota a 2048, que es el tope que acepta la ruta.
 */
export function miniatura(url: string, anchoCss: number): string {
  const ancho = Math.min(2048, Math.round(anchoCss * 2));
  return `${url}${url.includes('?') ? '&' : '?'}w=${ancho}`;
}

/**
 * Una imagen de la galería: miniatura para dibujar, original al hacer clic.
 *
 * ── Por qué un enlace y no un visor dentro de la página ───────────────────────
 * Porque una foto que se abre en su propia pestaña se puede hacer zoom, guardar, arrastrar a
 * otra ventana y comparar con otra pestaña al lado. Un visor propio tendría que reimplementar
 * las cuatro cosas y ninguna quedaría tan bien.
 *
 * ── Por qué el enlace tampoco abre el original ────────────────────────────────
 * Porque abrirlo tardaba segundos y se notaba. Las imágenes viven en KV —R2 nunca se habilitó
 * en esta cuenta— y leer 1,35 MB de ahí se midió en 2,3 s; encima el original no pasa por el
 * transformador, así que tampoco entra en la caché y ese costo se paga en CADA ampliación.
 *
 * Se abre un WebP de 1600 px: a simple vista es indistinguible del PNG de 1024×1280 que se
 * guardó, pesa unas siete veces menos, y sale de la misma caché que las miniaturas. El original
 * sigue disponible por la misma ruta sin `?w=` — es lo que bajan las plataformas al publicar.
 */
export function Toma({
  url,
  alt,
  ancho,
  className,
}: {
  url: string;
  alt: string;
  /** Ancho al que se dibuja, en píxeles de CSS. Decide qué se pide, no cómo se maqueta. */
  ancho: number;
  className?: string;
}) {
  return (
    <a
      href={miniatura(url, 800)}
      target="_blank"
      // `noreferrer` además de `noopener`: la URL lleva la firma de acceso en la query, y no
      // tiene por qué viajar como referente a ningún lado.
      rel="noopener noreferrer"
      className="block cursor-zoom-in"
    >
      <img src={miniatura(url, ancho)} alt={alt} className={className} loading="lazy" />
    </a>
  );
}
